import type { ComponentProps, ComponentType, FC, ReactElement } from "react";
import { useState } from "react";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import type { SxProps, Theme } from "@mui/material/styles";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import Autocomplete from "@mui/material/Autocomplete";
import type { RefCallBack } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import type { InputProps } from "../DynamicInput";
import type { IconProps } from "../../shared/icons";
import {
  AutocompleteOption,
  createFilterAutocompleteOption,
  CreateNewAutocompleteOption,
  withCreateNewOption,
  type AutocompleteOptionWithMaybeIsNew,
} from "../../shared/Autocomplete";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import type { Option } from "../types";
import type { InlineInputState } from "../inlineForm/inputs/InlineInputState";
import { useInlineInputState } from "../inlineForm/inputs/InlineInputState";
import { DynamicShared } from "../../dynamicShared";
import { useDynamicFormState } from "../DynamicFormProvider";
import { trackEvent } from "../../shared/Snowplow";
import type { CreateNewDialogProps, CreateNewDialogState } from "./MultiAutocomplete";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";

type SingleAutocompleteProps = InputProps<string> & {
  options: Option[];
  renderIcon?: (props: IconProps, option?: Option) => ReactElement | undefined;
  inlineInputStateStyles?: Record<InlineInputState, SxProps<Theme>>;
  placeholderKey: TranslationKey;
  chipVariant: ComponentProps<typeof DynamicShared.Chip>["variant"];
  denseOptions?: boolean;
} & (
    | { noOptionsTextKey: TranslationKey; CreateNewDialog?: undefined }
    | { noOptionsTextKey?: undefined; CreateNewDialog: ComponentType<CreateNewDialogProps> }
  );

export const SingleAutocomplete: FC<SingleAutocompleteProps> = props => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const {
    questionCard,
    readonly,
    renderIcon,
    inlineInputStateStyles,
    chipVariant,
    noOptionsTextKey,
    CreateNewDialog,
    denseOptions,
    onSave,
  } = props;

  const { field } = useController({ control, name: questionCard.id });
  const { inputState, setIsEditing } = useInlineInputState(readonly);
  const [createNewDialogState, setCreateNewDialogState] = useState<CreateNewDialogState>({ isOpen: false });
  const saveMutation = useMutation({
    mutationFn: async (value: string | null) => {
      setValue(questionCard.id, value, { shouldValidate: true, shouldDirty: true });
      setIsEditing(false);
      return onSave(value);
    },
  });

  const options: AutocompleteOptionWithMaybeIsNew[] = props.options;
  const selectedOption = options.find(option => option.value === field.value) ?? null;

  return (
    <>
      <Autocomplete
        noOptionsText={noOptionsTextKey ? t(noOptionsTextKey) : undefined}
        disabled={readonly || saveMutation.isPending}
        openOnFocus
        clearOnBlur
        clearOnEscape
        id={questionCard.id}
        blurOnSelect
        onBlur={() => setIsEditing(false)}
        onFocus={() => setIsEditing(true)}
        filterOptions={(sourceOption, params) => {
          const filtered = createFilterAutocompleteOption(sourceOption, params);
          return CreateNewDialog ? withCreateNewOption(filtered, params.inputValue, t) : filtered;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return option.isNew ? (
            <CreateNewAutocompleteOption key={option.value} {...optionProps} option={option} />
          ) : (
            <AutocompleteOption key={option.value} {...optionProps} option={option} dense={denseOptions} />
          );
        }}
        value={selectedOption}
        onChange={(_, option) => {
          if (option && "isNew" in option) {
            trackEvent.createNewOptionFromForm(questionCard.input.type);
            setCreateNewDialogState({ isOpen: true, value: option.value });
          } else saveMutation.mutate(option ? option.value : null);
        }}
        options={options}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        renderInput={params => {
          return (
            <AutoCompleteTextField
              params={formatParams({ params, chipVariant, selectedOption, inputState })}
              isSaving={saveMutation.isPending}
              inputRef={field.ref}
              onBlur={field.onBlur}
              isError={saveMutation.isError}
              isMandatory={questionCard.mandatory}
              inputState={inputState}
              isEmpty={isAnswerRequestEmpty(selectedOption?.value)}
              placeholderKey={"placeholderKey" in props ? props.placeholderKey : undefined}
              renderIcon={renderIcon}
              inlineInputStateStyles={inlineInputStateStyles}
            />
          );
        }}
      />
      {createNewDialogState.isOpen && CreateNewDialog && (
        <CreateNewDialog
          onClose={() => setCreateNewDialogState({ isOpen: false })}
          onCreated={option => saveMutation.mutate(option.value)}
          value={createNewDialogState.value}
        />
      )}
    </>
  );
};

type FormatParams = (args: {
  params: AutocompleteRenderInputParams;
  chipVariant: ComponentProps<typeof DynamicShared.Chip>["variant"];
  selectedOption: Option | null;
  inputState: InlineInputState;
}) => AutocompleteRenderInputParams;

const formatParams: FormatParams = ({ params, chipVariant, selectedOption, inputState }) => {
  if (chipVariant === "entity") return params;
  return {
    ...params,
    slotProps: {
      ...params.slotProps,
      htmlInput: {
        ...params.slotProps.htmlInput,
        value: inputState === "editing" ? params.slotProps.htmlInput.value : "",
      },
      input: {
        ...params.slotProps.input,
        startAdornment: (
          <>
            {params.slotProps.input.startAdornment}
            {selectedOption && inputState !== "editing" && (
              <DynamicShared.Chip label={selectedOption.label} inputState={inputState} variant={chipVariant} />
            )}
          </>
        ),
      },
    },
  };
};

type AutoCompleteTextFieldProps = {
  params: AutocompleteRenderInputParams;
  isSaving: boolean;
  isError: boolean;
  inputState: InlineInputState;
  inputRef: RefCallBack;
  onBlur: () => void;
  isEmpty: boolean;
  placeholderKey: TranslationKey | undefined;
  renderIcon?: SingleAutocompleteProps["renderIcon"];
  inlineInputStateStyles?: SingleAutocompleteProps["inlineInputStateStyles"];
  sx?: any;
  isMandatory?: boolean;
};
export const AutoCompleteTextField: FC<AutoCompleteTextFieldProps> = ({
  params,
  onBlur,
  inputRef,
  inputState,
  inlineInputStateStyles,
  placeholderKey,
  isSaving,
  isError,
  renderIcon,
  isEmpty,
  sx,
  isMandatory,
}) => {
  const { t } = useTranslation();
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();

  const showErrorIndicator = !isSaving && isError;

  return (
    <FormControl fullWidth>
      <TextField
        {...params}
        size="small"
        onBlur={onBlur}
        inputRef={inputRef}
        placeholder={isEmpty && inputState !== "editing" && placeholderKey ? t(placeholderKey) : ""}
        slotProps={{
          ...params.slotProps,
          input: {
            ...params.slotProps.input,
            sx: { ...inlineInputStateStyles?.[inputState], ...sx },

            startAdornment: (
              <>
                {inputState !== "editing" &&
                  !isEmpty &&
                  renderIcon?.({ size: 16, color: theme => theme.palette.text.primary })}
                {params.slotProps.input.startAdornment}
              </>
            ),
          },
        }}
      />
      {showErrorIndicator && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && isMandatory && isEmpty && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </FormControl>
  );
};
